import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Modal, Field, TextInput, Select, FormActions, dbErrorMessage } from './form';

const CURRENCIES = ['IDR', 'EUR', 'USD'] as const;
const METHODS = ['bank_transfer', 'cash', 'card', 'other'] as const;
type Opt = { id: string; name: string };
type Line = { id?: string; description: string; quantity: string; unit_price: string };
type Base = { open: boolean; onClose: () => void; onSaved: (id?: string) => void };

// ── New invoice (manual / from contract / from work order) ───────────────────
export function InvoiceModal({ open, onClose, onSaved }: Base) {
  const { t, i18n } = useTranslation();
  const { id: orgId, currency: orgCur } = useOrg();
  const [mode, setMode] = useState<'manual' | 'contract' | 'work_order'>('manual');
  const [clients, setClients] = useState<Opt[]>([]);
  const [properties, setProperties] = useState<(Opt & { client_id: string | null })[]>([]);
  const [contracts, setContracts] = useState<Opt[]>([]);
  const [wos, setWos] = useState<Opt[]>([]);
  const [taxes, setTaxes] = useState<(Opt & { rate_pct: number })[]>([]);
  const [f, setF] = useState({ client_id: '', property_id: '', contract_id: '', wo_id: '', tax_rate_id: '', currency: orgCur, margin: '0', due_date: '' });
  const [lines, setLines] = useState<Line[]>([{ description: '', quantity: '1', unit_price: '' }]);
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return; setErr({}); setMode('manual'); setLines([{ description: '', quantity: '1', unit_price: '' }]);
    (async () => {
      const [cl, pr, ct, wo, tx] = await Promise.all([
        supabase.from('clients').select('id,name').is('deleted_at', null).order('name'),
        supabase.from('properties').select('id,name,client_id').is('deleted_at', null).order('name'),
        supabase.from('contracts').select('id,name,contract_number').is('deleted_at', null).order('created_at', { ascending: false }),
        supabase.from('work_orders').select('id,wo_number,billing_status').eq('billing_status', 'billable').is('deleted_at', null).order('created_at', { ascending: false }).limit(50),
        supabase.from('tax_rates').select('id,name,rate_pct').is('deleted_at', null).eq('active', true).order('name'),
      ]);
      setClients((cl.data as Opt[]) ?? []); setProperties((pr.data as any[]) ?? []);
      setContracts(((ct.data as any[]) ?? []).map((c) => ({ id: c.id, name: `${c.contract_number ?? ''} ${c.name}` })));
      setWos(((wo.data as any[]) ?? []).map((w) => ({ id: w.id, name: w.wo_number ?? w.id.slice(0, 8) })));
      setTaxes((tx.data as any[]) ?? []);
      setF({ client_id: '', property_id: '', contract_id: '', wo_id: '', tax_rate_id: '', currency: orgCur, margin: '0', due_date: '' });
    })();
  }, [open]);

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setErr({});
    try {
      let invId: string | null = null;
      if (mode === 'contract') {
        if (!f.contract_id) { setErr({ _: t('form.errRequired') }); return; }
        const { data, error } = await supabase.rpc('create_invoice_from_contract', { p_contract: f.contract_id, p_tax_rate: f.tax_rate_id || null });
        if (error) { setErr({ _: dbErrorMessage(t, error) }); return; }
        invId = data as string;
      } else if (mode === 'work_order') {
        if (!f.wo_id) { setErr({ _: t('form.errRequired') }); return; }
        const { data, error } = await supabase.rpc('create_invoice_from_work_order', { p_wo: f.wo_id, p_margin_pct: Number(f.margin) || 0, p_tax_rate: f.tax_rate_id || null });
        if (error) { setErr({ _: dbErrorMessage(t, error) }); return; }
        invId = data as string;
      } else {
        const valid = lines.filter((l) => l.description.trim());
        if (!valid.length) { setErr({ _: t('quote.needLine') }); return; }
        const saved = await supabase.from('invoices').insert({ organization_id: orgId, client_id: f.client_id || null, property_id: f.property_id || null, currency: f.currency, tax_rate_id: f.tax_rate_id || null, source: 'manual', status: 'draft' } as any).select('id').single();
        if (saved.error) { setErr({ _: dbErrorMessage(t, saved.error) }); return; }
        invId = (saved.data as any).id;
        let pos = 0;
        for (const l of valid) await supabase.from('invoice_lines').insert({ organization_id: orgId, invoice_id: invId, description: l.description.trim(), quantity: Number(l.quantity) || 1, unit_price: Number(l.unit_price) || 0, position: pos++ } as any);
      }
      if (invId && f.due_date) await supabase.from('invoices').update({ due_date: f.due_date }).eq('id', invId);
      onSaved(invId ?? undefined); onClose();
    } finally { setBusy(false); }
  }

  const total = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);

  return (
    <Modal open={open} onClose={onClose} title={t('inv.new')} wide>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <div className="flex gap-2">
          {(['manual', 'contract', 'work_order'] as const).map((m) => (
            <button type="button" key={m} onClick={() => setMode(m)} className={`flex-1 rounded-md border px-3 py-2 text-sm ${mode === m ? 'border-accent bg-accent/10 text-accent' : 'border-border text-muted'}`}>{t('inv.mode_' + m)}</button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          {mode === 'contract' && <Field label={t('nav.contracts')}><Select value={f.contract_id} onChange={(e) => setF({ ...f, contract_id: e.target.value })}><option value="">—</option>{contracts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>}
          {mode === 'work_order' && <>
            <Field label={t('nav.workOrders')}><Select value={f.wo_id} onChange={(e) => setF({ ...f, wo_id: e.target.value })}><option value="">—</option>{wos.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}</Select></Field>
            <Field label={t('inv.margin')}><TextInput type="number" min="0" step="0.1" value={f.margin} onChange={(e) => setF({ ...f, margin: e.target.value })} /></Field>
          </>}
          {mode === 'manual' && <>
            <Field label={t('nav.clients')}><Select value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })}><option value="">—</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
            <Field label={t('nav.properties')}><Select value={f.property_id} onChange={(e) => setF({ ...f, property_id: e.target.value })}><option value="">—</option>{properties.filter((p) => !f.client_id || p.client_id === f.client_id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</Select></Field>
            <Field label={t('cat.currency')}><Select value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>{CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}</Select></Field>
          </>}
          <Field label={t('inv.tax')}><Select value={f.tax_rate_id} onChange={(e) => setF({ ...f, tax_rate_id: e.target.value })}><option value="">{t('inv.noTax')}</option>{taxes.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.rate_pct}%)</option>)}</Select></Field>
          <Field label={t('inv.dueDate')}><TextInput type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></Field>
        </div>

        {mode === 'manual' && (
          <div className="flex flex-col gap-2 rounded-md border border-divider p-3">
            <div className="flex items-center justify-between"><span className="text-xs font-medium text-muted">{t('quote.lines')}</span><Button type="button" variant="ghost" size="sm" onClick={() => setLines([...lines, { description: '', quantity: '1', unit_price: '' }])}>+ {t('cost.add')}</Button></div>
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr_70px_110px_auto] items-center gap-2">
                <TextInput value={l.description} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} placeholder={t('cost.description')} />
                <TextInput type="number" min="0" step="0.5" value={l.quantity} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))} />
                <TextInput type="number" min="0" step="0.01" value={l.unit_price} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, unit_price: e.target.value } : x))} placeholder={t('cat.unitPrice')} />
                <button type="button" onClick={() => setLines(lines.filter((_, j) => j !== i))} className="px-2 text-muted hover:text-st-overdue-fg">✕</button>
              </div>
            ))}
            <div className="flex justify-end font-mono text-sm">{t('cost.total')}: {formatCurrency(total, f.currency, i18n.language)}</div>
          </div>
        )}
        <FormActions onCancel={onClose} busy={busy} submitLabel={t('inv.create')} />
      </form>
    </Modal>
  );
}

// ── Record a payment ─────────────────────────────────────────────────────────
export function PaymentModal({ open, onClose, onSaved, invoiceId, orgId, currency, balance }: Base & { invoiceId: string; orgId: string; currency: string; balance: number }) {
  const { t, i18n } = useTranslation();
  const [f, setF] = useState({ amount: '', method: 'bank_transfer', paid_on: '', reference: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { if (open) setF({ amount: String(balance || ''), method: 'bank_transfer', paid_on: '', reference: '' }); }, [open, balance]);

  async function submit(e: React.FormEvent) {
    e.preventDefault(); if (!(Number(f.amount) > 0)) { setErr(t('form.errRequired')); return; }
    setBusy(true);
    const { error } = await supabase.from('payments').insert({ organization_id: orgId, invoice_id: invoiceId, amount: Number(f.amount), currency, method: f.method, paid_on: f.paid_on || undefined, reference: f.reference.trim() || null } as any);
    setBusy(false);
    if (error) { setErr(dbErrorMessage(t, error)); return; }
    onSaved(); onClose();
  }
  return (
    <Modal open={open} onClose={onClose} title={t('inv.recordPayment')}>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err}</div>}
        <Field label={`${t('inv.amount')} (${currency})`} hint={`${t('contract.price')}: ${formatCurrency(balance, currency, i18n.language)}`}><TextInput type="number" min="0" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} autoFocus /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('inv.method')}><Select value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })}>{METHODS.map((m) => <option key={m} value={m}>{t('inv.pm_' + m)}</option>)}</Select></Field>
          <Field label={t('inv.paidOn')}><TextInput type="date" value={f.paid_on} onChange={(e) => setF({ ...f, paid_on: e.target.value })} /></Field>
        </div>
        <Field label={t('inv.reference')}><TextInput value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} /></Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}
